import React from "react";
import {
  BsCheckCircle,
  BsPeople,
  BsCollection,
  BsLink45Deg,
  BsAward,
  BsCurrencyDollar,
  BsFloppy,
} from "react-icons/bs";
import { HiOutlineSparkles } from "react-icons/hi";
import { getChain } from "../../utils/currencies";

// Category labels
const categoryLabels = {
  desci_general: "DeSci General",
  biotech_life_sciences: "Biotech & Life Sciences",
  ai_computer_science: "AI & Computer Science",
  open_source_software: "Open Source Software",
  women_in_science: "Women in Science",
  regional: "Regional",
  climate_environment: "Climate & Environment",
  space_astronomy: "Space & Astronomy",
  other: "Other",
};

// Tier colors
const tierColors = {
  bronze: "from-amber-600 to-amber-400",
  silver: "from-gray-400 to-gray-300",
  gold: "from-yellow-500 to-yellow-300",
  platinum: "from-slate-600 to-slate-400",
  custom: "from-purple-600 to-purple-400",
};

// Perk labels
const perkLabels = {
  privateChannels: "Private Channels",
  priorityInvestment: "Priority Investment",
  votingRights: "Voting Rights",
  exclusiveEvents: "Exclusive Events",
  futureAirdrops: "Future Airdrops",
  earlyResearchAccess: "Early Research Access",
  specialRole: "Special Role",
  merchandise: "Merchandise",
  partnerDiscounts: "Partner Discounts",
};

const Step6Preview = ({ formik, onPublish, publishing, publishingStep, isPublished, onSaveEdits, savingEdits }) => {
  const values = formik.values;

  // Count active perks
  const activeChecklistPerks = Object.entries(values.perkChecklist || {})
    .filter(([_, active]) => active)
    .map(([key]) => perkLabels[key] || key);

  const customPerks = (values.perks || []).map((p) => p.title);
  const allPerks = [...activeChecklistPerks, ...customPerks];

  // Count filled links
  const filledLinks = Object.entries(values.links || {}).filter(
    ([_, val]) => val && val.trim() !== ""
  );

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center mb-6 md:mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-green-100 to-emerald-100 mb-3">
          <HiOutlineSparkles className="text-2xl md:text-3xl text-green-600" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
          {isPublished ? "Review & Save" : "Preview & Confirm"}
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          {isPublished ? "Review your changes before saving" : "Review your community before publishing"}
        </p>
      </div>

      {/* NFT Preview Card */}
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Artwork */}
          <div className="relative">
            {values.bannerImage ? (
              <img
                src={values.bannerImage}
                alt="Banner"
                className="w-full h-32 object-cover"
              />
            ) : (
              <div className="w-full h-32 bg-gradient-to-r from-purple-600 to-blue-600" />
            )}
            <div className="absolute -bottom-16 left-1/2 -translate-x-1/2">
              <div className="w-32 h-32 rounded-2xl border-4 border-white shadow-lg overflow-hidden bg-gray-100">
                {values.artwork ? (
                  <img
                    src={values.artwork}
                    alt="NFT Artwork"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <BsCollection className="text-3xl" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="pt-20 pb-6 px-6">
            {/* Tier Badge */}
            {values.membershipTier && (
              <div className="flex justify-center mb-3">
                <span
                  className={`px-4 py-1 rounded-full text-xs text-white font-medium bg-gradient-to-r ${
                    tierColors[values.membershipTier]
                  } capitalize`}
                >
                  {values.membershipTier} Tier
                </span>
              </div>
            )}

            {/* Name */}
            <h3 className="text-xl font-bold text-center text-gray-800">
              {values.name || "Community Name"}
            </h3>

            {/* Category */}
            <p className="text-center text-gray-500 text-sm mt-1">
              {categoryLabels[values.category] || "Category"}
            </p>

            {/* Description */}
            <p className="text-center text-gray-600 text-sm mt-3 line-clamp-2">
              {values.shortDescription || "Short description..."}
            </p>

            {/* Stats */}
            <div className="flex justify-around mt-6 py-4 border-y border-gray-100">
              <div className="text-center">
                <p className="text-xl font-bold text-primary">
                  {values.totalSupply || 0}
                </p>
                <p className="text-xs text-gray-500">Supply</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-primary">
                  {(Number(values.saleSettings?.price) || 0) === 0 ? "Free" : values.saleSettings.price}
                </p>
                <p className="text-xs text-gray-500">
                  {(Number(values.saleSettings?.price) || 0) === 0 ? "Membership" : (values.saleSettings?.currency || "ETH")}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-primary">{allPerks.length}</p>
                <p className="text-xs text-gray-500">Perks</p>
              </div>
            </div>

            {/* Links Count */}
            <div className="flex items-center justify-center gap-2 mt-4 text-gray-500 text-sm">
              <BsLink45Deg />
              <span>{filledLinks.length} community links</span>
            </div>
          </div>
        </div>
      </div>

      {/* Details Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Perks Summary */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BsAward className="text-primary" />
            Member Perks ({allPerks.length})
          </h4>
          {allPerks.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {allPerks.map((perk, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 bg-purple-50 text-purple-700 text-xs rounded-full"
                >
                  {perk}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No perks defined</p>
          )}
        </div>

        {/* Sale Settings Summary */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BsCurrencyDollar className="text-primary" />
            Sale Settings
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Sale Type:</span>
              <span className="font-medium capitalize">
                {values.saleSettings?.type?.replace("_", " ") || "Fixed"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Price:</span>
              <span className="font-medium">
                {(Number(values.saleSettings?.price) || 0) === 0
                  ? "Free"
                  : `${values.saleSettings.price} ${values.saleSettings?.currency || "ETH"}`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Royalties:</span>
              <span className="font-medium">{values.royalties || 5}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Membership:</span>
              <span className="font-medium capitalize">
                {values.membershipDuration || "Lifetime"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Blockchain:</span>
              <span className="font-medium capitalize">
                {getChain({ network: values.blockchain })?.name || values.blockchain || "Not selected"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="bg-green-50 rounded-2xl p-6">
        <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
          <BsCheckCircle />
          Ready to Publish Checklist
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { label: "Community name", ok: !!values.name },
            { label: "Short description", ok: !!values.shortDescription },
            { label: "NFT artwork", ok: !!values.artwork },
            { label: "Category selected", ok: !!values.category },
            { label: "At least one link", ok: filledLinks.length > 0 },
            { label: "Total supply set", ok: values.totalSupply > 0 },
            { label: "Price configured", ok: values.saleSettings?.price >= 0 },
            { label: "Blockchain selected", ok: !!values.blockchain },
          ].map((item, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 text-sm ${
                item.ok ? "text-green-700" : "text-red-600"
              }`}
            >
              {item.ok ? (
                <BsCheckCircle className="text-green-500" />
              ) : (
                <span className="w-4 h-4 rounded-full border-2 border-red-400" />
              )}
              {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* Important Notice */}
      {isPublished ? (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
          <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <BsFloppy />
            Saving Your Changes
          </h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Changes to descriptions, links, perks, and images will be saved immediately</li>
            <li>• On-chain fields (name, supply, price, blockchain) remain unchanged</li>
            <li>• No wallet transaction required — this is an off-chain update</li>
          </ul>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
          <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
            <HiOutlineSparkles />
            Before You Publish
          </h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• Once published, total supply cannot be changed</li>
            <li>• NFT artwork and core details are permanent on-chain</li>
            <li>• Ensure your wallet has enough funds for gas fees</li>
            <li>• You'll sign transactions to create the NFT collection</li>
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col md:flex-row gap-4 justify-center pt-4">
        {isPublished ? (
          <button
            type="button"
            onClick={onSaveEdits}
            disabled={savingEdits}
            className="px-8 py-3 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white font-medium rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 min-w-[200px]"
          >
            {savingEdits ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <BsFloppy />
                Save Edits
              </>
            )}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => formik.submitForm()}
              className="px-8 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
            >
              Save as Draft
            </button>
            <button
              type="button"
              onClick={onPublish}
              disabled={publishing}
              className="px-8 py-3 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white font-medium rounded-xl hover:opacity-90 transition-opacity flex flex-col items-center justify-center gap-1 disabled:opacity-50 min-w-[200px]"
            >
              {publishing ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Publishing...
                  </div>
                  {publishingStep && (
                    <span className="text-xs opacity-80">{publishingStep}</span>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <HiOutlineSparkles />
                  Publish Community
                </div>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Step6Preview;
